import { Link } from "react-router-dom"
import {
  FlaskConical, Languages, Wrench, Palette, BookOpen, GraduationCap,
  ArrowRight, CheckCircle2, CalendarDays, ClipboardList, Award, Users, Beaker,
} from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { PageHero } from "../../components/site/PageHero"
import { Card, CardContent } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"

const programmeCategories = [
  {
    icon: FlaskConical,
    title: "Sciences",
    description:
      "Our science programmes prepare future teachers of chemistry, physics, biology, mathematics, integrated science and computer science. Students receive rigorous theoretical grounding combined with extensive laboratory practicals, micro-teaching and field-based learning that equip them to deliver effective science instruction at the basic and senior secondary levels.",
    courses: ["Chemistry / Mathematics", "Physics / Mathematics", "Biology / Integrated Science", "Computer Science / Mathematics", "Mathematics / Physics"],
    accent: "from-brand-600 to-brand-800",
  },
  {
    icon: Languages,
    title: "Languages",
    description:
      "Language education at YMS COE emphasises linguistic competence, literary appreciation and pedagogical skill. Programmes cover English, Hausa, Arabic and French, with strong components in oral communication, contemporary literature and language teaching methodology that prepare graduates to teach in diverse multilingual classrooms.",
    courses: ["English / Hausa", "English / History", "Arabic / Islamic Studies", "French / English", "Hausa / Islamic Studies"],
    accent: "from-gold-500 to-gold-700",
  },
  {
    icon: Wrench,
    title: "Vocational & Technical",
    description:
      "Vocational education trains competent teachers of technical and entrepreneurial subjects including agricultural science, business education, home economics and fine arts. Hands-on workshops, demonstration farms and industry partnerships ensure graduates can deliver practical, income-generating skills to their future students.",
    courses: ["Agricultural Science", "Business Education", "Home Economics", "Fine & Applied Arts", "Technical Education"],
    accent: "from-brand-700 to-brand-900",
  },
  {
    icon: Palette,
    title: "Arts & Social Sciences",
    description:
      "These programmes produce teachers of humanities and social science subjects including history, geography, social studies, economics and political science. Coursework blends disciplinary depth with pedagogical training so that graduates can facilitate critical thinking, citizenship and civic responsibility in their classrooms.",
    courses: ["Social Studies / Geography", "History / Geography", "Economics / Political Science", "Geography / Mathematics", "Social Studies / CRS"],
    accent: "from-gold-600 to-gold-800",
  },
  {
    icon: BookOpen,
    title: "Education Foundations",
    description:
      "All NCE students take a common core of education courses covering educational psychology, philosophy, sociology, curriculum studies, measurement and evaluation, guidance and counselling, and special needs education. These courses form the professional backbone of every graduate's identity as a teacher.",
    courses: ["Educational Psychology", "Philosophy of Education", "Curriculum Studies", "Measurement & Evaluation", "Guidance & Counselling", "Special Needs Education"],
    accent: "from-brand-800 to-brand-950",
  },
]

const nceFeatures = [
  { icon: Award, title: "NCCE Accredited", text: "All programmes fully accredited by the National Commission for Colleges of Education." },
  { icon: CalendarDays, title: "3 Years / 6 Semesters", text: "Comprehensive curriculum balancing subject mastery with professional education." },
  { icon: ClipboardList, title: "Teaching Practice", text: "Mandatory supervised school placement in NCE 2 second semester (12 weeks)." },
  { icon: Users, title: "Small Class Sizes", text: "Average 1:30 staff-to-student ratio enabling personalised academic mentoring." },
]

export default function Programs() {
  return (
    <SiteShell>
      <PageHero
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Programmes" }]}
        subtitle="Academic Programmes"
        title="NCE Programmes Designed for the Educators of Tomorrow"
        description="The Nigeria Certificate in Education (NCE) is the foundational qualification for teaching in Nigeria. Our programmes are structured across five faculties to give every future teacher deep subject knowledge, professional training and practical classroom experience."
      />

      {/* NCE Overview */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <Badge variant="success" className="mb-3">The NCE Programme</Badge>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100 mb-5 leading-tight">
                A Rigorous Pathway into the Teaching Profession
              </h2>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-4">
                The Nigeria Certificate in Education (NCE) is the minimum academic qualification recognised by the Federal Government for teaching in basic and senior secondary schools. At YMS COE, the NCE programme runs for three academic years (six semesters) and combines subject-content courses, professional education courses, general studies and supervised teaching practice.
              </p>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-6">
                Every student selects a teaching subject combination from two disciplines, complemented by a common Education core. This structure ensures that graduates are not only knowledgeable in their subjects but also grounded in the science and art of teaching — able to design lessons, manage classrooms, assess learning and adapt instruction to diverse learners.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/admission-requirements">
                  <Button className="bg-brand-800 dark:bg-brand-700">
                    Admission Requirements <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/fees">
                  <Button variant="outline">View Fees</Button>
                </Link>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {nceFeatures.map(f => (
                <Card key={f.title} className="border-0 shadow-md">
                  <CardContent className="pt-5">
                    <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-900/40 grid place-items-center mb-3">
                      <f.icon className="w-5 h-5 text-brand-700 dark:text-brand-400" />
                    </div>
                    <h3 className="font-display font-bold text-sm text-ink-900 dark:text-ink-100 mb-1">{f.title}</h3>
                    <p className="text-xs text-ink-500 dark:text-ink-400 leading-relaxed">{f.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Programme categories */}
      <section className="py-16 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="warning" className="mb-3">Explore by Faculty</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100">Programme Categories</h2>
            <p className="text-ink-500 dark:text-ink-400 mt-3 max-w-2xl mx-auto">
              Our NCE programmes are organised across five faculties. Each combines subject mastery with the professional training required of effective teachers.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {programmeCategories.map(p => (
              <Card key={p.title} className="card-lift border-0 shadow-md overflow-hidden">
                <div className={`h-1.5 bg-gradient-to-r ${p.accent}`} />
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${p.accent} grid place-items-center shrink-0 shadow-md`}>
                      <p.icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-xl text-ink-900 dark:text-ink-100">{p.title}</h3>
                      <p className="text-xs text-ink-500 dark:text-ink-400 mt-0.5">Sample course combinations below</p>
                    </div>
                  </div>
                  <p className="text-sm text-ink-600 dark:text-ink-300 leading-relaxed mb-4">{p.description}</p>
                  <div className="space-y-2">
                    {p.courses.map(c => (
                      <div key={c} className="flex items-start gap-2 text-sm text-ink-700 dark:text-ink-300">
                        <CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" />
                        <span>{c}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pre-NCE & Remedial */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?q=80&w=1200&auto=format&fit=crop"
                  alt="Classroom"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-gold-500 text-ink-950 p-6 rounded-2xl shadow-2xl hidden md:block">
                <GraduationCap className="w-8 h-8 mb-2" />
                <div className="font-display text-2xl font-bold">1 Year</div>
                <div className="text-xs uppercase tracking-wider opacity-80">Foundation Programme</div>
              </div>
            </div>
            <div>
              <Badge variant="warning" className="mb-3">Foundation Pathway</Badge>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100 mb-5 leading-tight">
                Pre-NCE & Remedial Studies
              </h2>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-4">
                The Pre-NCE programme is a one-year remedial course designed for candidates who do not yet meet the direct entry requirements for the NCE. It provides intensive coaching in O-Level subjects, study skills and an introduction to education, giving students a second chance to qualify for tertiary teacher education.
              </p>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-6">
                Students who successfully complete the Pre-NCE with the required grade progress directly into the NCE programme without needing to reapply. The programme runs in two semesters and is open to candidates with at least three O-Level credit passes including English or Mathematics.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 rounded-lg bg-brand-50 dark:bg-brand-900/20">
                  <Beaker className="w-5 h-5 text-brand-700 dark:text-brand-400 mb-1.5" />
                  <div className="font-semibold text-sm text-ink-900 dark:text-ink-100">Intensive Coaching</div>
                  <div className="text-xs text-ink-500 dark:text-ink-400">English, Maths & 2 subjects</div>
                </div>
                <div className="p-4 rounded-lg bg-gold-50 dark:bg-gold-900/20">
                  <Award className="w-5 h-5 text-gold-700 dark:text-gold-400 mb-1.5" />
                  <div className="font-semibold text-sm text-ink-900 dark:text-ink-100">Direct Progression</div>
                  <div className="text-xs text-ink-500 dark:text-ink-400">Guaranteed NCE admission</div>
                </div>
              </div>
              <Link to="/admission-requirements">
                <Button className="bg-brand-800 dark:bg-brand-700">
                  Pre-NCE Requirements <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Teaching Practice + Admission links */}
      <section className="py-16 bg-brand-950 dark:bg-ink-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-20" />
        <div className="container mx-auto px-4 relative">
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/teaching-practice" className="group">
              <Card className="border-0 bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 transition-colors h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-gold-500/20 grid place-items-center">
                      <ClipboardList className="w-6 h-6 text-gold-300" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-white/60 group-hover:text-gold-300 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-display font-bold text-xl mb-2">Teaching Practice</h3>
                  <p className="text-sm text-white/75 leading-relaxed">
                    All NCE students undertake a 12-week supervised teaching practice in a partner school during NCE 2. Learn about the objectives, process, supervision and assessment of this critical professional experience.
                  </p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/admission-requirements" className="group">
              <Card className="border-0 bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 transition-colors h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-brand-500/20 grid place-items-center">
                      <GraduationCap className="w-6 h-6 text-brand-300" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-white/60 group-hover:text-gold-300 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-display font-bold text-xl mb-2">Admission Requirements</h3>
                  <p className="text-sm text-white/75 leading-relaxed">
                    Find detailed entry requirements for the NCE, Pre-NCE and direct entry candidates, the documents you need to submit, and a step-by-step guide through the application process.
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>
    </SiteShell>
  )
}
